import * as i0 from '@angular/core';
import { Pipe, Injectable, NgModule } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * This pipe allows an object child property to be retrieved by a string path separated by a period (.)
 * For example: employee.name to retrieve the name property of an employee object
 *
 * This pipe is useful for display purposes, in particular, for grids.
 */
class ObjectByStringPipe {
    transform(object, path) {
        try {
            path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
            path = path.replace(/^\./, ''); // strip a leading dot
            const a = path.split('.');
            for (let i = 0, n = a.length; i < n; ++i) {
                const k = a[i];
                if (k in object) {
                    object = object[k];
                }
                else {
                    return;
                }
            }
            return object;
        }
        catch (e) {
            // Ignore errors as this is just used to display nested properties
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: ObjectByStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.6", ngImport: i0, type: ObjectByStringPipe, name: "objectByString" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: ObjectByStringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'objectByString',
                    pure: true,
                }]
        }] });

/**
 * This pipe allows an object child property to be retrieved by a string path separated by a period (.)
 * For example: employee.name to retrieve the name property of an employee object
 *
 * This pipe is useful for display purposes, in particular, for grids.
 */
class BooleanByStringPipe {
    transform(object, path) {
        const p = new ObjectByStringPipe();
        return Boolean(JSON.parse(p.transform(object, path)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: BooleanByStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.6", ngImport: i0, type: BooleanByStringPipe, name: "booleanByString" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: BooleanByStringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'booleanByString',
                    pure: true,
                }]
        }] });

class ResizeService {
    get onResize$() {
        return this.resizeSubject.asObservable();
    }
    constructor() {
        this.destroy$ = new Subject();
        this.resizeSubject = new Subject();
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
            this.resizeSubject.next(event.target);
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: ResizeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: ResizeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: ResizeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class CommonModule {
    static forRoot() {
        return {
            ngModule: CommonModule,
            providers: [ResizeService]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: CommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.6", ngImport: i0, type: CommonModule, declarations: [BooleanByStringPipe, ObjectByStringPipe], exports: [BooleanByStringPipe, ObjectByStringPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: CommonModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.6", ngImport: i0, type: CommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BooleanByStringPipe, ObjectByStringPipe],
                    imports: [],
                    exports: [BooleanByStringPipe, ObjectByStringPipe]
                }]
        }] });

/**
 * A collection of helper methods to work with dates
 * @author David Yee
 * @author Dan Leder
 */
class DateHelper {
    /**
     * Iterates through the keys of the object looking for string values that match ISO8601 or yyyy-MM-dd date strings,
     * converting them into JavaScript Date objects.
     * @param object The object to convert its date strings.
     * @returns The converted object with date strings as JavaScript dates.
     */
    static ConvertObjectStringDateKeysToDateObjects(object) {
        const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(?:Z|(\+|-)([\d|:]*))?$/;
        if (object == null) {
            return object;
        }
        Object.keys(object).map(key => {
            if (typeof object[key] === 'string') {
                const a = reISO.exec(object[key]);
                if (a) {
                    const date = new Date(object[key]);
                    if (!isNaN(date.getTime())) {
                        object[key] = date;
                    }
                }
                else if (key.toLocaleLowerCase().indexOf('date') > -1) {
                    // Handle yyyy-MM-dd only when field name contains 'date'
                    const dateOnlyRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
                    // @ts-ignore
                    const [, year, month, day] = dateOnlyRegex.exec(object[key]) || [];
                    if (year !== undefined && month !== undefined && day !== undefined) {
                        object[key] = new Date(Number(year), Number(month) - 1, Number(day));
                    }
                }
            }
            else if (typeof object[key] === 'object' && object[key] != null) {
                this.ConvertObjectStringDateKeysToDateObjects(object[key]);
            }
        });
        return object;
    }
    /**
     * Get the days between two dates
     * @param startDate Beginning date
     * @param endDate End date
     * @returns A list of dates representing the days between the two dates.
     */
    static EnumerateDaysBetweenDates(startDate, endDate) {
        const startDateCopy = new Date(startDate);
        const endDateCopy = new Date(endDate);
        startDateCopy.setHours(0, 0, 0, 0);
        endDateCopy.setHours(0, 0, 0, 0);
        const difference = endDateCopy.getTime() - startDateCopy.getTime();
        const totalDays = Math.ceil(difference / (1000 * 3600 * 24));
        let currentDate = startDateCopy;
        let direction = 1; // normally, add days
        if (totalDays < 0) {
            direction = -1;
        } // if startDate < endDate, then go backwards
        const dates = [startDateCopy];
        for (let i = 0; i < totalDays; ++i) {
            const clone = new Date(currentDate.getTime());
            clone.setDate(clone.getDate() + direction);
            currentDate = clone;
            dates.push(clone);
        }
        // // Replace the momentjs version with the above
        // // This allows us to drop momentjs dependency
        // //
        // const currDate = moment(startDate).startOf('day');
        // const lastDate = moment(endDate).startOf('day');
        // dates.push(currDate.toDate());
        // while (currDate.add(1, 'days').diff(lastDate) <= 0) {
        //   dates.push(currDate.clone().toDate());
        // }
        //
        return dates;
    }
    /**
     * Compare two dates and check if they represent the same day (ignoring time)
     * @param date1 The first date to use in comparison.
     * @param date2 The second date to use in comparison.
     * @returns true if the same day; false otherwise.
     */
    static IsSameDay(date1, date2) {
        // See https://flaviocopes.com/how-to-check-dates-same-day-javascript/
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
        // // Avoid momentjs dependency:
        // return moment(date1).isSame(moment(date2), 'day');
    }
    static ParseTimeSpan(json) {
        const reISO = /P((\d{1,})Y)?((\d{1,})M)?((\d{1,})D)?T((\d{1,})H)?((\d{1,})M)?((\d{1,})S)?/;
        if (typeof json === 'string') {
            const a = reISO.exec(json);
            if (a) {
                const date = new Date();
                date.setHours(a[8] ? Number(a[8]) : 0);
                date.setMinutes(a[10] ? Number(a[10]) : 0);
                date.setSeconds(a[12] ? Number(a[12]) : 0);
                if (!isNaN(date.getTime())) {
                    json = date;
                }
            }
        }
        return json;
    }
}

/*
 * Public API Surface of common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BooleanByStringPipe, CommonModule, DateHelper, ObjectByStringPipe, ResizeService };
